import React, { useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import Input from "components/form/Input"
import PhotoSelector from "components/form/PhotoSelector"
import Loading from "modules/loader-watchers/Loading"
import { useTranslation } from "react-i18next"
import { serialize } from "object-to-formdata"
import { isEqual } from "lodash"
import useForm from "hooks/useForm"
import moment from "moment/moment"
import { useDispatch, useSelector } from "react-redux"
import { parseExpenseReceipt, saveExpense, addCategory, checkDuplicateExpense } from "store/accounting"
import { metadataSelector } from "store/selectors"
import styles from "./Form.module.css"
import AddCategoryModal from "./AddCategoryModal"
import ExpenseLineItems from "./ExpenseLineItems"
import DuplicateExpenseModal from "./DuplicateExpenseModal"
import { Button, Col, Flex, Row, Select, DatePicker, Tag, Checkbox } from "antd"
import { Text } from "ui/Typography"
import { toaster } from "components/common/Toast"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import Icon from "components/common/Icon"
import dayjs from "dayjs"

const recurringType = new Set(["monthly", "yearly"])

const Form = ({ onChanged, onSuccess, expenseId, expense, setVisible }) => {
  const [isOpenAddCategoryModal, setIsOpenAddCategoryModal] = useState(false)
  const [isExpenseItemsChanged, setIsExpenseItemsChanged] = useState(false)
  const [categoryName, setCategoryName] = useState("")
  const [isUploadReceipt, setisUploadReceipt] = useState(false)
  const [receiptImageUrl, setReceiptImageUrl] = useState([])
  const [expenseAmountError, setExpenseAmountError] = useState("")
  const [expenseAmountInfo, setExpenseAmountInfo] = useState("")
  const [totalExpenseItemAmount, setTotalExpenseItemAmount] = useState(0)
  const [isSaving, setIsSaving] = useState(false)
  const [invalidReceiptMessage, setInvalidReceiptMessage] = useState("")
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
  const [duplicates, setDuplicates] = useState([])
  const [pendingPayload, setPendingPayload] = useState(null)
  const [isShowOverrideAmountCheckbox, setisShowOverrideAmountCheckbox] = useState(false)
  const [isChecked, setIsChecked] = useState(false)
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const receiptParsing = useSelector((store) => store?.accounting.receiptParsing)
  const metadata = useSelector(metadataSelector("accounting"))
  const merchantName = expense?.merchant_name
  const initialPurchaseDate = expense?.purchase_date
    ? moment(expense.purchase_date, ["YYYY-MM-DD", "MM/DD/YYYY", moment.ISO_8601]).format("MM/DD/YYYY")
    : moment().format("MM/DD/YYYY")

  const [form, changeHandler, submitHandler, submitCallback, , isChanged] = useForm(
    {
      ...expense,
      expense_category_id: expense?.category?.id,
      photos: expenseId === undefined || expense?.receipts[0]?.placeholder_url === null ? [] : expense?.receipts,
      merchant_name: merchantName,
      purchase_date: initialPurchaseDate
    },
    ["expense_category_id", "merchant_id", "merchant_name", "amount", "purchase_date", "recurring_type", "recurring", "notes", "photos"]
  )
  const [expenseItems, setExpenseItems] = useState([{ name: "", amount: "", taxes_fees: "", expense_category_id: "" }])
  const receipts = useMemo(() => form.photos?.filter(({ _destroy }) => !_destroy), [form.photos])

  useEffect(() => {
    if (expense?.expense_line_items && Array.isArray(expense?.expense_line_items)) {
      setExpenseItems(expense?.expense_line_items?.map((item) => ({ ...item })))
    }
  }, [expense?.expense_line_items])

  useEffect(() => {
    if (Array.isArray(expense?.receipts) && expense.receipts.length > 0) {
      setReceiptImageUrl(expense.receipts.map((receipt) => ({ url: receipt?.url, id: receipt?.id })))
    } else {
      setReceiptImageUrl([])
    }
  }, [expense?.receipts])

  const handleExpenseItemChange = (idx, field, value) => {
    setExpenseItems((prev) => {
      const updated = prev.map((item, i) => (i === idx ? { ...item, [field]: value } : item))
      changeHandler({ target: { name: "items", value: updated } })
      return updated
    })
    setIsExpenseItemsChanged(true)
  }

  const handleAddExpenseItem = () => {
    setExpenseItems((prev) => [...prev, { merchant_name: "", amount: "", expense_category_id: "" }])
  }

  const handleRemoveExpenseItem = (idx) => {
    setExpenseItems((prev) =>
      prev
        .map((item, i) => {
          if (i !== idx) return item
          if (item.id) return { ...item, _destroy: true }
          return null
        })
        .filter((item) => item !== null)
    )
    setIsExpenseItemsChanged(true)
  }

  const handleDownloadReceipt = useCallback(async () => {
    const receipt = receipts?.[0]
    if (!receipt) return

    const fileName =
      receipt?.file?.name || receipt?.original_filename || receipt?.file_name || receipt?.filename || receipt?.name || "receipt"

    if (receipt?.file instanceof File) {
      const url = window.URL.createObjectURL(receipt.file)
      const link = document.createElement("a")
      link.href = url
      link.download = fileName
      link.rel = "noopener"
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      return
    }

    const url = receipt?.file_preview || receipt?.thumbnail_url || receipt?.preview_url || receipt?.url
    if (!url) return

    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = blobUrl
      link.download = fileName
      link.rel = "noopener"
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(blobUrl)
    } catch {
      window.open(url, "_blank")
    }
  }, [receipts])

  const handleRemoveReceipt = useCallback(() => {
    const destroyedPhotos = Array.isArray(form.photos) ? form.photos.map((p) => ({ ...p, _destroy: true })) : []
    changeHandler({ target: { name: "photos", value: destroyedPhotos } })
    setReceiptImageUrl((prev) => {
      if (Array.isArray(prev)) return prev.map((r) => ({ ...r, _destroy: true }))
      if (prev && typeof prev === "object") return [{ ...prev, _destroy: true }]
      return []
    })
    setisUploadReceipt(false)
    setInvalidReceiptMessage("")
  }, [form.photos, changeHandler])

  useEffect(() => {
    const hasReceiptContext = isUploadReceipt || (expense?.receipts?.length > 0 && expense?.receipts?.[0]?.url != null)
    if (form.amount && expenseItems?.length) {
      const totalExpenseAmount = expenseItems.reduce((sum, item) => {
        if (!item._destroy && item.amount) return sum + Number.parseFloat(item.amount)
        return sum
      }, 0)
      setTotalExpenseItemAmount(totalExpenseAmount)
      const itemTotal = totalExpenseAmount.toFixed(2)
      const receiptAmount = Number.parseFloat(form.amount).toFixed(2)

      if (hasReceiptContext) {
        const itemTotalNum = Number.parseFloat(itemTotal)
        const receiptAmountNum = Number.parseFloat(receiptAmount)
        if (itemTotalNum > receiptAmountNum) {
          setisShowOverrideAmountCheckbox(true)
          setExpenseAmountError(t("expense.error_message", { itemTotal, receiptAmount }))
          setExpenseAmountInfo("")
        } else if (itemTotalNum < receiptAmountNum) {
          setisShowOverrideAmountCheckbox(true)
          setExpenseAmountInfo(t("expense.warning_message", { itemTotal, receiptAmount }))
          setExpenseAmountError("")
        } else {
          setExpenseAmountError("")
          setExpenseAmountInfo("")
          setisShowOverrideAmountCheckbox(false)
        }
      } else {
        setisShowOverrideAmountCheckbox(false)
        setExpenseAmountError("")
        setExpenseAmountInfo("")
      }
    } else {
      setisShowOverrideAmountCheckbox(false)
      setExpenseAmountError("")
      setExpenseAmountInfo("")
    }
  }, [form.amount, expenseItems, isUploadReceipt, expense?.receipts?.length, t])

  const handleExecuteSave = useCallback(
    (payload) => {
      setIsSaving(true)
      dispatch(saveExpense(expense?.id, payload))
        .then((res) => {
          setVisible(false)
          if (res?.message) toaster.success(res.message)
          onSuccess?.()
        })
        .finally(() => setIsSaving(false))
    },
    [dispatch, expense?.id, onSuccess, setVisible]
  )

  submitCallback(() => {
    setIsSaving(true)
    const items = expenseItems.map((item) =>
      item._destroy
        ? { id: item.id, _destroy: true }
        : {
            ...(item.id && { id: item.id }),
            name: item.name,
            amount: item.amount === "" ? 0 : Number(item.amount),
            expense_category_id: item.expense_category_id
          }
    )
    const total = expenseItems.filter((item) => !item._destroy).reduce((sum, item) => sum + Number.parseFloat(item.amount || 0), 0)
    const roundedTotal = Number.parseFloat(total.toFixed(2))
    const hasExistingReceipt = expense?.receipts && expense.receipts.length > 0 && expense.receipts[0]?.url != null && !isChecked
    const effectiveAmount =
      isExpenseItemsChanged || !hasExistingReceipt ? roundedTotal : Number(Number.parseFloat(form.amount || 0).toFixed(2))
    const formattedPurchaseDate = form.purchase_date
      ? moment(form.purchase_date, ["MM/DD/YYYY", moment.ISO_8601]).format("YYYY-MM-DD")
      : null
    const payload = {
      merchant_name: form.merchant_name,
      recurring: form.recurring || false,
      recurring_type: form.recurring ? form.recurring_type : "",
      amount: effectiveAmount,
      purchase_date: formattedPurchaseDate,
      currency: form.currency,
      notes: form.notes,
      expense_line_items_attributes: items,
      destroy_all: isUploadReceipt,
      receipts_attributes: receiptImageUrl
    }

    const isRecurring = form.recurring
    const validRecurringType = recurringType.has(form.recurring_type)
    if (isRecurring && !validRecurringType) {
      toaster.error(t("expense.recurring_type_error"))
      setIsSaving(false)
      return
    }

    if (
      !form.merchant_name ||
      !form.purchase_date ||
      !expenseItems.length ||
      expenseItems.some((item) => !item.name || !item.amount || !item.expense_category_id)
    ) {
      toaster.error("Please fill all required fields.")
      setIsSaving(false)
      return
    }

    if (expenseAmountError && !isChecked) {
      setIsSaving(false)
      return
    }

    dispatch(
      checkDuplicateExpense({
        purchase_date: formattedPurchaseDate,
        amount: effectiveAmount,
        ...(expense?.id && { expense_id: expense.id })
      })
    )
      .then((res) => {
        if (res?.has_duplicates && res.duplicates?.length > 0) {
          setPendingPayload(payload)
          setDuplicates(res.duplicates)
          setIsDuplicateModalOpen(true)
          setIsSaving(false)
        } else {
          handleExecuteSave(payload)
        }
      })
      .catch(() => handleExecuteSave(payload))
  })

  useEffect(() => {
    if (!receipts?.length || isEqual(expense?.receipts, receipts)) return
    const { file } = receipts[0]
    if (!file) return
    const formData = serialize({ file })
    dispatch(parseExpenseReceipt(formData))
      .then((res) => {
        const data = res?.data || res
        if (!data) throw new Error("Empty receipt parse response")
        setReceiptImageUrl([{ image: data?.image_url }])
        setisUploadReceipt(true)
        if (data.amount) changeHandler({ target: { name: "amount", value: data.amount } })
        if (data.purchase_date) {
          const formattedDate = moment(data.purchase_date, ["YYYY-MM-DD", moment.ISO_8601]).format("MM/DD/YYYY")
          changeHandler({ target: { name: "purchase_date", value: formattedDate } })
        }
        if (data.merchant_name) changeHandler({ target: { name: "merchant_name", value: data.merchant_name } })
        if (Array.isArray(data.items) && data.items.length > 0) {
          setInvalidReceiptMessage("")
          setExpenseItems(data.items.map((item) => ({ name: item.name || "", amount: item.amount || "", expense_category_id: "" })))
        } else {
          setInvalidReceiptMessage(data.message || t("expense.invalid_receipt"))
          setExpenseItems([{ name: "", amount: "", expense_category_id: "" }])
        }
      })
      .catch(() => {
        changeHandler({ target: { name: "photos", value: [] } })
        setReceiptImageUrl([])
        setisUploadReceipt(false)
      })
  }, [receipts?.length])

  useEffect(() => {
    if (typeof onChanged === "function") onChanged(isChanged || isExpenseItemsChanged)
  }, [isChanged, isExpenseItemsChanged, onChanged])

  const itemTotalRounded = Number(totalExpenseItemAmount.toFixed(2))
  const parsedAmount = Number.parseFloat(form.amount)
  const formAmountRounded = Number(Number.isNaN(parsedAmount) ? 0 : Number.parseFloat(parsedAmount.toFixed(2)))
  const hasReceiptContext = expense?.receipts?.length > 0 && expense?.receipts?.[0]?.url != null
  const disableOverItemTotal = hasReceiptContext && itemTotalRounded > formAmountRounded

  const handleAddCategory = useCallback(async () => {
    const result = await dispatch(addCategory({ expense_category: { name: categoryName } }))
    if (!result?.error) {
      toaster.success(result?.message)
      setCategoryName("")
      setIsOpenAddCategoryModal(false)
    }
  }, [dispatch, categoryName])

  return (
    <FirstLoading name="accounting.expense" new={!expense?.loading}>
      <form onSubmit={(e) => submitHandler(e, isExpenseItemsChanged)}>
        <div>
          <Flex vertical gap={20}>
            <Row gutter={[20, 20]}>
              <Col span={12}>
                <Flex vertical gap={6}>
                  <Text fw="500">{t("expense.merchant_name")}*</Text>
                  <Input
                    id="merchant_name"
                    type="text"
                    name="merchant_name"
                    placeholder="Merchant Name"
                    value={form.merchant_name || ""}
                    onChange={changeHandler}
                    className="fs-6"
                  />
                </Flex>
              </Col>
              <Col span={12}>
                <Flex vertical gap={6}>
                  <Text fw="500">{t("expense.date_of_purchase")}*</Text>
                  <DatePicker
                    name="purchase_date"
                    value={form?.purchase_date ? dayjs(form.purchase_date) : dayjs()}
                    className="w-full p-xs"
                    placeholder="MM/DD/YYYY"
                    format="MM/DD/YYYY"
                    disabledDate={(current) => current && current > Date.now()}
                    onChange={(_, dateString) => changeHandler({ target: { name: "purchase_date", value: dateString } })}
                  />
                </Flex>
              </Col>
            </Row>

            <ExpenseLineItems
              expenseItems={expenseItems}
              onItemChange={handleExpenseItemChange}
              onAddItem={handleAddExpenseItem}
              onRemoveItem={handleRemoveExpenseItem}
              categories={metadata.expense_categories}
              onOpenAddCategory={() => setIsOpenAddCategoryModal(true)}
            />

            <Flex gap={30} wrap>
              <Flex vertical gap={6}>
                <Text fw="500">{t("expense.upload_receipt")}</Text>
                <Loading loading={receiptParsing} className="me-auto">
                  <PhotoSelector imageHeight={90} form={form} changeHandler={changeHandler} small start label={false} max={1} />
                </Loading>
                {receipts?.length > 0 && !receiptParsing && (
                  <div className="mt-2 hstack gap-3">
                    <Button className={styles.removeReceiptButton} color="primary" variant="link" onClick={handleRemoveReceipt}>
                      {t("expense.remove_picture", { defaultValue: "Remove Picture" })}
                    </Button>
                    <Button className={styles.removeReceiptButton} color="primary" variant="link" onClick={handleDownloadReceipt}>
                      {t("booking.receipt.download", { defaultValue: "Download receipt" })}
                    </Button>
                  </div>
                )}
                {invalidReceiptMessage && (
                  <Tag color="error" className="p-xs mt-2 pre-wrap">
                    {invalidReceiptMessage}
                  </Tag>
                )}
              </Flex>
              <div>
                <Flex gap={10} align="center" className="mb-xs">
                  <Checkbox
                    label=""
                    name="recurring"
                    value="true"
                    checked={[true, "true"].includes(form.recurring)}
                    onChange={(e) => {
                      if (!e.target.checked) changeHandler({ target: { name: "recurring_type", value: undefined } })
                      changeHandler(e)
                    }}
                  >
                    {t("expense.recurring_receipt")}
                  </Checkbox>
                </Flex>
                <Select
                  value={form.recurring ? form.recurring_type : undefined}
                  onChange={(value) => changeHandler({ target: { name: "recurring_type", value } })}
                  style={{ minWidth: 220 }}
                  options={[
                    { value: "monthly", label: t("expense.monthly") },
                    { value: "yearly", label: t("expense.yearly") }
                  ]}
                  placeholder={t("expense.select_recurring_type", { defaultValue: "Select type" })}
                  disabled={![true, "true"].includes(form.recurring)}
                  suffixIcon={<Icon iconName="SidebarToggle" size={16} className={styles.recurringDropdown} />}
                  popupClassName="recurring-type-dropdown"
                  size="large"
                />
              </div>
            </Flex>

            <div>
              <Text fw="500" className="pb-xxs block">
                {t("expense.notes")}
              </Text>
              <Input
                id="notes"
                type="textarea"
                name="notes"
                rows={10}
                placeholder={t("expense.add_notes")}
                value={form.notes || ""}
                onChange={changeHandler}
                className="fs-6"
                withError
              />
            </div>

            {isShowOverrideAmountCheckbox && (
              <Row gutter={[20, 20]}>
                <Col span={24}>
                  <Flex gap={10} align="center" className="h-full">
                    <Input
                      type="checkbox"
                      name="override_amount"
                      value="true"
                      checked={isChecked}
                      onChange={(e) => {
                        setIsChecked(e.target.checked)
                        changeHandler(e)
                      }}
                      className="mt-0"
                    />
                    <span className="text-dark">Override amount</span>
                  </Flex>
                </Col>
              </Row>
            )}
          </Flex>

          {expenseAmountError && !isChecked && (
            <Tag color="error" className={`p-xs ${styles.warningContainer}`}>
              {expenseAmountError}
            </Tag>
          )}
          {!expenseAmountError && expenseAmountInfo && !isChecked && (
            <Tag color="orange" className={`p-xs ${styles.warningContainer}`}>
              {expenseAmountInfo}
            </Tag>
          )}

          <Flex justify="end" gap={10} className="pt-m">
            <Button
              htmlType="submit"
              color="primary"
              variant="solid"
              disabled={
                isSaving ||
                receiptParsing ||
                expense?.loading ||
                (!isChecked && !isChanged && !isExpenseItemsChanged) ||
                (!isChecked && disableOverItemTotal)
              }
            >
              {t("expense.save_changes")}
            </Button>
          </Flex>
        </div>
      </form>

      {isOpenAddCategoryModal && (
        <AddCategoryModal
          visible={isOpenAddCategoryModal}
          onCancel={() => {
            setIsOpenAddCategoryModal(false)
            setCategoryName("")
          }}
          setCategoryName={setCategoryName}
          categoryName={categoryName}
          onAdd={handleAddCategory}
        />
      )}

      <DuplicateExpenseModal
        open={isDuplicateModalOpen}
        onCancel={() => setIsDuplicateModalOpen(false)}
        onSave={() => {
          setIsDuplicateModalOpen(false)
          handleExecuteSave(pendingPayload)
        }}
        isSaving={isSaving}
        duplicates={duplicates}
      />
    </FirstLoading>
  )
}

Form.propTypes = {
  onChanged: PropTypes.func,
  onSuccess: PropTypes.func,
  expenseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  expense: PropTypes.object,
  setVisible: PropTypes.func
}

export default Form
